import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  ArrowRight, Loader2, CheckCircle2, Sparkles,
  BarChart3, Users, Wallet, Clock, MapPin,
} from "lucide-react";
import { analyzeHiringScan, unlockHiringScan, createContact } from "@/lib/api";
import HiringScanReport from "@/components/HiringScanReport";
import GdprConsent from "@/components/GdprConsent";
import Seo from "@/components/Seo";
import { trackLead } from "@/lib/pixel";
import { getUtmPayload } from "@/lib/utm";

const MOCKUP = "/images/hiring-scan-mockup.png";

const STATS = [
  { n: "+300", l: "Figure reclutate" },
  { n: "+500", l: "Aziende aiutate" },
  { n: "< 30 g", l: "Time to hire medio" },
  { n: "+30", l: "Persone nel team" },
];

const RUOLI_CONTATTO = [
  "CEO / Founder",
  "Direttore HR",
  "Responsabile di funzione",
  "COO",
  "Operations Manager",
  "Altro",
];

const BENEFITS = [
  { icon: BarChart3, t: "Difficulty Score", d: "quanto è difficile trovare questa figura nel mercato italiano oggi." },
  { icon: Users, t: "Profili disponibili", d: "quante persone con quel profilo esistono nell'area che cerchi." },
  { icon: Wallet, t: "RAL media", d: "cosa si aspetta di guadagnare chi ha quel ruolo e quella seniority." },
  { icon: Clock, t: "Time to hire stimato", d: "quanto ci vuole mediamente, e quanto ci vuole con Lovac." },
  { icon: MapPin, t: "Dove si trovano", d: "da quali settori vengono, quanto sono mobili, come si raggiungono." },
];

const EMPTY_SCAN = {
  ruolo: "", seniority: "Senior", settore: "", area: "", dimensione_azienda: "",
};
const EMPTY_GATE = {
  nome: "", cognome: "", azienda: "", ruolo_contatto: "", email: "", telefono: "",
};

export default function OptinScan() {
  const [step, setStep] = useState(1);
  const [scan, setScan] = useState(EMPTY_SCAN);
  const [gate, setGate] = useState(EMPTY_GATE);
  const [gdpr, setGdpr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);
  const [fee, setFee] = useState(null);

  const goToStep2 = (e) => {
    e.preventDefault();
    if (!scan.ruolo || !scan.settore || !scan.area || !scan.dimensione_azienda) {
      toast.error("Compila tutti i campi");
      return;
    }
    setStep(2);
    // smooth scroll to the form on step transition
    setTimeout(() => {
      document.getElementById("form")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 50);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const req = ["nome", "cognome", "azienda", "ruolo_contatto", "email", "telefono"];
    for (const k of req) {
      if (!gate[k]) {
        toast.error("Compila tutti i dati di contatto");
        return;
      }
    }
    if (!gdpr) {
      toast.error("Devi accettare la Privacy Policy per procedere");
      return;
    }

    setLoading(true);
    try {
      const utm = getUtmPayload();
      const result = await analyzeHiringScan(scan);
      const unlocked = await unlockHiringScan({
        scan_id: result.scan_id,
        nome: gate.nome,
        cognome: gate.cognome,
        azienda: gate.azienda,
        ruolo_contatto: gate.ruolo_contatto,
        email: gate.email,
        telefono: gate.telefono,
        ...utm,
      });
      createContact({
        nome: gate.nome,
        cognome: gate.cognome,
        azienda: gate.azienda,
        email: gate.email,
        telefono: gate.telefono,
        ruolo: gate.ruolo_contatto,
        ruolo_cercato: scan.ruolo,
        dimensione_azienda: scan.dimensione_azienda,
        settore_aziendale: scan.settore,
        ...utm,
        sorgente: "lp-hiring-scan-optin",
      }).catch(() => {});
      trackLead({ content_name: "Hiring Scan (LP)", content_category: "lp-hiring-scan" });
      setReport(result);
      setFee(unlocked);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error("Errore. Riprova.");
    } finally {
      setLoading(false);
    }
  };

  const resetAll = () => {
    setScan(EMPTY_SCAN);
    setGate(EMPTY_GATE);
    setReport(null);
    setFee(null);
    setStep(1);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  /* -------- REPORT STATE -------- */
  if (report) {
    return (
      <HiringScanReport
        scan={scan}
        report={report}
        fee={fee}
        onRestart={resetAll}
      />
    );
  }

  return (
    <>
      <Seo title="Hiring Scan gratuito, Lovac" description="Report mercato per la figura che cerchi: difficulty score, profili disponibili, RAL, time to hire." path="/lp/hiring-scan" noindex />
      {/* MINI-HEADER, solo logo */}
      <div className="bg-lovac-dark border-b border-lovac-dark">
        <div className="container-lovac px-6 py-5 flex items-center">
          <img src="/images/logo-orizzontale.png" alt="Lovac" className="h-7 sm:h-8" />
        </div>
      </div>

      {/* HERO + FORM */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            background: "radial-gradient(60% 50% at 50% 0%, rgba(0,196,140,0.10) 0%, rgba(0,196,140,0) 60%)",
          }}
        />
        <div className="container-lovac px-6 py-20 lg:py-24 relative z-10">
          <div className="grid lg:grid-cols-[1fr_1.1fr] gap-14 lg:gap-16 items-start">

            {/* LEFT, copy */}
            <div className="lg:sticky lg:top-24">
              <span className="badge-on-dark">Hiring Scan by Lovac, gratuito</span>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-7 text-on-dark leading-[1.05]">
                Scopri quanto è difficile<br />
                <span className="text-lovac-accent">trovare la figura che stai cercando.</span>
              </h1>
              <p className="text-on-dark-muted mt-7 text-base sm:text-lg leading-relaxed max-w-xl">
                Inserisci il ruolo e ottieni <strong className="text-lovac-accent font-semibold">in 60 secondi</strong> un report gratuito con:
                difficulty score, profili disponibili nel mercato italiano,
                RAL media e time to hire stimato.
              </p>
              <div className="mt-10 flex items-start gap-3 max-w-md">
                <Sparkles size={18} className="text-lovac-accent mt-1 shrink-0" />
                <p className="text-sm text-on-dark-muted leading-relaxed">
                  Generato su dati pubblici e osservatori di settore. Usato da <span className="text-on-dark">+500 aziende italiane</span>.
                </p>
              </div>
            </div>

            {/* RIGHT, form a step */}
            <form
              id="form"
              onSubmit={step === 1 ? goToStep2 : onSubmit}
              className="space-y-5"
              data-testid="optin-form"
              data-nocapture="true"
              data-ghl-ignore="true"
              data-lc-ignore="true"
            >
              {/* Progress */}
              <div className="flex items-center gap-3 mb-2 px-1">
                <div className="flex items-center gap-2">
                  <span
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-mono ${
                      step >= 1
                        ? "bg-lovac-accent text-lovac-dark"
                        : "bg-[rgba(255,255,255,0.06)] text-on-dark-muted"
                    }`}
                  >
                    {step > 1 ? "✓" : "1"}
                  </span>
                  <span className={`text-xs uppercase tracking-wider ${step === 1 ? "text-on-dark" : "text-on-dark-muted"}`}>
                    Cosa cerchi
                  </span>
                </div>
                <span className="h-px flex-1 bg-[rgba(255,255,255,0.08)]" />
                <div className="flex items-center gap-2">
                  <span
                    className={`h-7 w-7 rounded-full flex items-center justify-center text-xs font-mono ${
                      step === 2
                        ? "bg-lovac-accent text-lovac-dark"
                        : "bg-[rgba(255,255,255,0.06)] text-on-dark-muted"
                    }`}
                  >
                    2
                  </span>
                  <span className={`text-xs uppercase tracking-wider ${step === 2 ? "text-on-dark" : "text-on-dark-muted"}`}>
                    I tuoi dati
                  </span>
                </div>
              </div>

              {/* SEZIONE 1, Dati del report */}
              {step === 1 && (
                <div
                  className="rounded-2xl p-7 sm:p-8"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "0.5px solid rgba(0,196,140,0.25)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">Sezione 1 · Cosa cerchi</p>
                  <h2 className="font-display text-2xl text-on-dark mt-1">Genera subito il report</h2>

                  <div className="mt-6 space-y-3">
                    <div>
                      <label className="text-xs text-on-dark-muted">Ruolo cercato</label>
                      <input
                        className="input-on-dark mt-1.5"
                        placeholder="es. Architetto Software"
                        value={scan.ruolo}
                        onChange={(e) => setScan({ ...scan, ruolo: e.target.value })}
                        data-testid="optin-scan-role"
                      />
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-on-dark-muted">Seniority</label>
                        <select
                          className="input-on-dark mt-1.5"
                          value={scan.seniority}
                          onChange={(e) => setScan({ ...scan, seniority: e.target.value })}
                          data-testid="optin-scan-seniority"
                        >
                          <option>Junior</option>
                          <option>Middle</option>
                          <option>Senior</option>
                          <option>C-level</option>
                        </select>
                      </div>
                      <div>
                        <label className="text-xs text-on-dark-muted">Settore aziendale</label>
                        <input
                          className="input-on-dark mt-1.5"
                          placeholder="es. Software B2B"
                          value={scan.settore}
                          onChange={(e) => setScan({ ...scan, settore: e.target.value })}
                          data-testid="optin-scan-sector"
                        />
                      </div>
                    </div>

                    <div className="grid sm:grid-cols-2 gap-3">
                      <div>
                        <label className="text-xs text-on-dark-muted">Area geografica</label>
                        <input
                          className="input-on-dark mt-1.5"
                          placeholder="es. Marche / Milano"
                          value={scan.area}
                          onChange={(e) => setScan({ ...scan, area: e.target.value })}
                          data-testid="optin-scan-area"
                        />
                      </div>
                      <div>
                        <label className="text-xs text-on-dark-muted">Dimensione azienda</label>
                        <select
                          className="input-on-dark mt-1.5"
                          value={scan.dimensione_azienda}
                          onChange={(e) => setScan({ ...scan, dimensione_azienda: e.target.value })}
                          data-testid="optin-scan-size"
                        >
                          <option value="">Seleziona…</option>
                          <option>1–10</option>
                          <option>11–50</option>
                          <option>51–250</option>
                          <option>250+</option>
                        </select>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* SEZIONE 2, Dati contatto */}
              {step === 2 && (
                <div
                  className="rounded-2xl p-7 sm:p-8"
                  style={{
                    background: "rgba(255,255,255,0.04)",
                    border: "0.5px solid rgba(0,196,140,0.25)",
                    backdropFilter: "blur(8px)",
                  }}
                >
                  <p className="text-[11px] uppercase tracking-wider text-on-dark-muted">A chi mandiamo il report?</p>
                  <h2 className="font-display text-2xl text-on-dark mt-1">Inserisci i tuoi dati</h2>

                  <div className="mt-6 space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <input
                        className="input-on-dark"
                        placeholder="Nome"
                        value={gate.nome}
                        onChange={(e) => setGate({ ...gate, nome: e.target.value })}
                        data-testid="optin-nome"
                      />
                      <input
                        className="input-on-dark"
                        placeholder="Cognome"
                        value={gate.cognome}
                        onChange={(e) => setGate({ ...gate, cognome: e.target.value })}
                        data-testid="optin-cognome"
                      />
                    </div>
                    <input
                      className="input-on-dark"
                      placeholder="Azienda"
                      value={gate.azienda}
                      onChange={(e) => setGate({ ...gate, azienda: e.target.value })}
                      data-testid="optin-azienda"
                    />
                    <select
                      className="input-on-dark"
                      value={gate.ruolo_contatto}
                      onChange={(e) => setGate({ ...gate, ruolo_contatto: e.target.value })}
                      data-testid="optin-ruolo"
                    >
                      <option value="">Il tuo ruolo</option>
                      {RUOLI_CONTATTO.map((r) => <option key={r} value={r}>{r}</option>)}
                    </select>
                    <input
                      type="email"
                      className="input-on-dark"
                      placeholder="Email aziendale"
                      value={gate.email}
                      onChange={(e) => setGate({ ...gate, email: e.target.value })}
                      data-testid="optin-email"
                    />
                    <input
                      className="input-on-dark"
                      placeholder="Telefono / WhatsApp"
                      value={gate.telefono}
                      onChange={(e) => setGate({ ...gate, telefono: e.target.value })}
                      data-testid="optin-tel"
                    />
                  </div>

                  <div className="mt-5 pt-5 border-t border-[rgba(255,255,255,0.08)]">
                    <GdprConsent checked={gdpr} onChange={setGdpr} variant="dark" testId="optin-gdpr" />
                  </div>

                  <button
                    type="button"
                    onClick={() => setStep(1)}
                    className="text-xs text-on-dark-muted hover:text-on-dark mt-4 underline-offset-2 hover:underline transition-colors"
                  >
                    ← Torna al passo precedente
                  </button>
                </div>
              )}

              {/* SUBMIT */}
              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full justify-center disabled:opacity-60"
                data-testid="optin-submit"
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="animate-spin" /> Genero il report…
                  </>
                ) : step === 1 ? (
                  <>
                    Continua <ArrowRight size={16} />
                  </>
                ) : (
                  <>
                    Genera il mio report gratuito <ArrowRight size={16} />
                  </>
                )}
              </button>

              <p className="text-[11px] text-on-dark-muted text-center leading-relaxed">
                Nessuno spam. Potresti essere contattato da un recruiter Lovac per approfondire la tua ricerca, solo se ha senso per te.
              </p>
            </form>
          </div>
        </div>
      </section>

      {/* COSA CONTIENE */}
      <section className="section bg-lovac">
        <div className="container-lovac grid lg:grid-cols-[1fr_1.1fr] gap-12 lg:gap-16 items-center">
          <div>
            <span className="badge-lovac">Cosa contiene</span>
            <h2 className="font-display text-4xl sm:text-5xl mt-5 text-lovac-dark leading-tight">
              Cosa trovi nel report.
            </h2>

            <ul className="mt-10 space-y-6 max-w-xl">
              {BENEFITS.map(({ icon: Icon, t, d }) => (
                <li key={t} className="flex items-start gap-4">
                  <div
                    className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0"
                    style={{ background: "rgba(0,196,140,0.14)", border: "0.5px solid rgba(0,196,140,0.25)" }}
                  >
                    <Icon size={16} className="text-lovac-accent" />
                  </div>
                  <div>
                    <p className="text-lg font-display text-lovac-dark leading-tight">{t}</p>
                    <p className="text-base text-lovac-muted mt-1.5 leading-relaxed">{d}</p>
                  </div>
                </li>
              ))}
            </ul>

            <a
              href="#form"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-primary mt-10 inline-flex"
            >
              Compila il form e genera il report <ArrowRight size={16} />
            </a>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-gradient-to-br from-[rgba(0,196,140,0.10)] via-transparent to-transparent rounded-3xl blur-2xl" />
            <img
              src={MOCKUP}
              alt="Esempio di report Hiring Scan Lovac su laptop"
              className="relative w-full h-auto"
              loading="lazy"
            />
          </div>
        </div>
      </section>

      {/* STATS BAR */}
      <section className="bg-lovac-dark text-on-dark grain relative overflow-hidden">
        <div className="container-lovac px-6 py-14">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-10 sm:gap-8 items-center">
            {STATS.map((s) => (
              <div key={s.l} className="text-center sm:text-left">
                <p className="font-display text-4xl sm:text-5xl text-on-dark leading-none">{s.n}</p>
                <p className="text-xs sm:text-sm text-on-dark-muted mt-2 uppercase tracking-wider">{s.l}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER CTA */}
      <section className="section bg-lovac">
        <div className="container-narrow text-center">
          <h2 className="font-display text-4xl sm:text-5xl text-lovac-dark leading-tight">
            Hai già in mente la figura che cerchi?
          </h2>
          <div className="flex justify-center mt-10">
            <a
              href="#form"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById("form")?.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className="btn-primary"
            >
              Genera il report gratuito <ArrowRight size={16} />
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
